import { Box, Flex, Icon, Link, Text } from '@chakra-ui/react';
import {
  Home,
  HomeOutlined,
  Inventory2Outlined,
  ListAltOutlined,
  PersonOutlined,
} from '@mui/icons-material';
import { useRouter } from 'next/router';
import { FC } from 'react';

import { RecipeIcon } from '../../recipes/icons';
import {
  BOTTOM_NAV_HEIGHT,
  BUYER_BOTTOM_NAV_ITEMS,
  BuyerBottomNavItemId,
  getActiveBuyerBottomNavId,
  shouldShowBuyerBottomNav,
} from './bottom-nav-config';

const ACTIVE_COLOR = '#0000a3';
const INACTIVE_COLOR = '#999999';
const BADGE_RED = '#ff0000';

interface BuyerBottomNavProps {
  notificationCount?: number;
}

const TabIcon: FC<{
  id: BuyerBottomNavItemId;
  isActive: boolean;
}> = ({ id, isActive }) => {
  const color = isActive ? ACTIVE_COLOR : INACTIVE_COLOR;
  const iconProps = { fontSize: '24px' as const, color };

  switch (id) {
    case 'home':
      return isActive ? (
        <Icon as={Home} {...iconProps} />
      ) : (
        <Icon as={HomeOutlined} {...iconProps} />
      );
    case 'orders':
      return <Icon as={ListAltOutlined} {...iconProps} />;
    case 'recipes':
      return <RecipeIcon {...iconProps} />;
    case 'shopping-list':
      return <Icon as={Inventory2Outlined} {...iconProps} />;
    case 'account':
      return <Icon as={PersonOutlined} {...iconProps} />;
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
};

export const BuyerBottomNav: FC<BuyerBottomNavProps> = ({
  notificationCount = 0,
}) => {
  const router = useRouter();
  const pathname = router.pathname;

  if (!shouldShowBuyerBottomNav(pathname)) {
    return null;
  }

  const activeId = getActiveBuyerBottomNavId(pathname);

  return (
    <Box
      as="nav"
      aria-label="Navigasi utama"
      {...{ 'data-testid': 'buyer-bottom-nav' }}
      display={{ base: 'block', md: 'none' }}
      position="fixed"
      right="0"
      bottom="0"
      left="0"
      zIndex="2"
      h={`${BOTTOM_NAV_HEIGHT}px`}
      bg="white"
      boxShadow="0px -4px 4px rgba(0, 0, 0, 0.05)"
      px="16px"
      pt="12px"
    >
      <Flex align="flex-start" justify="space-between" gap="12px" w="100%">
        {BUYER_BOTTOM_NAV_ITEMS.map((item) => {
          const isActive = activeId === item.id;
          const color = isActive ? ACTIVE_COLOR : INACTIVE_COLOR;

          return (
            <Link
              key={item.id}
              href={item.href}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="flex-start"
              w="48px"
              h="56px"
              position="relative"
              textDecoration="none"
              _hover={{ textDecoration: 'none' }}
              aria-current={isActive ? 'page' : undefined}
              aria-label={item.label}
            >
              <Box position="relative" w="24px" h="24px">
                <TabIcon id={item.id} isActive={isActive} />
                {item.showNewBadge ? (
                  <Box
                    position="absolute"
                    top="-2px"
                    left="-10px"
                    minW="24px"
                    h="10px"
                    px="4px"
                    borderRadius="8px"
                    bg={BADGE_RED}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text
                      as="span"
                      fontSize="8px"
                      lineHeight="10px"
                      color="white"
                      fontWeight="400"
                      letterSpacing="0.12px"
                    >
                      new
                    </Text>
                  </Box>
                ) : null}
                {item.showAccountDot && notificationCount > 0 ? (
                  <Box
                    position="absolute"
                    top="2px"
                    right="-2px"
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg={BADGE_RED}
                    aria-hidden="true"
                  />
                ) : null}
              </Box>
              <Text
                mt="0"
                w="48px"
                h="24px"
                fontSize="10px"
                lineHeight="24px"
                textAlign="center"
                color={color}
                fontWeight={isActive ? '500' : '400'}
                letterSpacing="0.04px"
                noOfLines={1}
              >
                {item.label}
              </Text>
              {isActive ? (
                <Box
                  position="absolute"
                  bottom="0"
                  left="0"
                  w="48px"
                  h="4px"
                  bg={BADGE_RED}
                  borderBottomRadius="8px"
                  aria-hidden="true"
                />
              ) : null}
            </Link>
          );
        })}
      </Flex>
    </Box>
  );
};

export default BuyerBottomNav;
